import { useEffect, useState } from "react";
import { useApi, useApp } from "../context/AppContext";

// Users (docs/tasks/35): who may sign in to this tenant's admin and what each
// may do. Tenant admins only. A change applies within 30 s, also to a session
// already open: the backend reads the user's record behind every token.

const ROLES = [
  ["tenant_admin", "Admin", "everything, including users, keys, releases and settings"],
  ["device_operator", "Device operator", "cabinets: door, test console, weight, console, layout updates"],
  ["ml_operator", "ML operator", "models, galleries, training sets, releases"],
  ["product_manager", "Product manager", "the product catalogue: photos, names, sizes"],
  ["auditor", "Auditor", "read only, including purchases and their evidence"],
];
const LABEL = Object.fromEntries(ROLES.map(([k, l]) => [k, l]));

function RolePicker({ value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {ROLES.map(([k, l, d]) => (
        <label key={k} className="flex items-center gap-1 text-xs" title={d}>
          <input type="checkbox" disabled={disabled} checked={value.includes(k)}
            onChange={(e) => onChange(e.target.checked ? [...value, k] : value.filter((x) => x !== k))} />
          {l}
        </label>
      ))}
    </div>
  );
}

function NewUser({ onDone }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", display_name: "", roles: ["device_operator"] });
  const [err, setErr] = useState(null);
  const [made, setMade] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    try {
      const r = await api.createUser(f);
      setMade({ email: f.email, sent: r.email_sent, password: r.temporary_password });
      setOpen(false); setF({ email: "", display_name: "", roles: ["device_operator"] }); onDone();
    } catch (ex) { setErr(String(ex.message || ex)); }
  };
  const result = made && (
    <div className={`card w-full text-sm ${made.sent ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
      {made.sent ? <>An invitation with a temporary password went to <strong>{made.email}</strong>. They choose their own password at the first sign-in.</>
        : <>The e-mail could not be sent. Give <strong>{made.email}</strong> this temporary password yourself (shown once): <code className="ml-1 rounded bg-white px-1 font-mono">{made.password}</code></>}
      <button className="ml-3 text-xs text-slate-600 hover:underline" onClick={() => setMade(null)}>close</button>
    </div>
  );
  if (!open) return <>{result}<button className="btn btn-primary" onClick={() => setOpen(true)}>Add a user</button></>;
  return (
    <form className="card w-full space-y-2" onSubmit={submit}>
      <div className="grid gap-2 md:grid-cols-3">
        <input className="input" type="email" required placeholder="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <input className="input" placeholder="name" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} />
      </div>
      <RolePicker value={f.roles} onChange={(roles) => setF({ ...f, roles })} />
      <div className="flex items-center gap-2">
        <button className="btn btn-primary text-xs" type="submit" disabled={!f.roles.length}>Create</button>
        <button className="btn btn-secondary text-xs" type="button" onClick={() => setOpen(false)}>Cancel</button>
        {err && <span className="text-xs text-rose-700">{err}</span>}
      </div>
      <p className="text-xs text-slate-500">A temporary password is made and e-mailed to them with the address of this admin; they choose their own at the first sign-in. Self-registration is closed.</p>
    </form>
  );
}

function UserRow({ u, me, onChanged }) {
  const api = useApi();
  const [roles, setRoles] = useState(u.roles);
  const [msg, setMsg] = useState(null);
  useEffect(() => setRoles(u.roles), [u]);
  const isMe = u.user_id === me;
  const send = async (body, done) => {
    setMsg(null);
    try { await api.updateUser(u.user_id, body); setMsg({ text: done }); onChanged(); }
    catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); }
  };
  const dirty = [...roles].sort().join() !== [...u.roles].sort().join();
  return (
    <tr className={u.status !== "active" ? "text-slate-400" : ""}>
      <td><div className="font-medium">{u.display_name || u.email}{isMe ? <span className="ml-1 text-xs text-sky-700">(you)</span> : null}</div><div className="text-xs text-slate-500">{u.email}</div></td>
      <td>
        <RolePicker value={roles} onChange={setRoles} disabled={u.status !== "active"} />
        {dirty && <button className="mt-1 text-xs text-sky-700 hover:underline" onClick={() => send({ roles }, "Roles saved.")}>save roles</button>}
      </td>
      <td className="text-xs">{u.status}{u.must_change_password ? <div className="text-amber-700">temporary password</div> : null}</td>
      <td className="text-xs">{u.created_at ? new Date(u.created_at * 1000).toLocaleDateString() : "—"}</td>
      <td className="space-x-2 whitespace-nowrap text-xs">
        {u.status === "active"
          ? !isMe && <button className="text-rose-700 hover:underline" onClick={() => window.confirm(`Disable ${u.email}? They are signed out within 30 s.`) && send({ status: "disabled" }, "Disabled.")}>disable</button>
          : <button className="text-sky-700 hover:underline" onClick={() => send({ status: "active" }, "Enabled.")}>enable</button>}
        <button className="text-sky-700 hover:underline" onClick={async () => {
          if (!window.confirm(`Reset the password of ${u.email}? A new temporary password is e-mailed to them; the old one stops working.`)) return;
          setMsg(null);
          try {
            const r = await api.updateUser(u.user_id, { reset_password: true });
            setMsg(r.email_sent ? { text: "A new temporary password was e-mailed." }
              : { bad: true, text: `The e-mail could not be sent; give them this temporary password: ${r.temporary_password}` });
            onChanged();
          } catch (ex) { setMsg({ bad: true, text: String(ex.message || ex) }); }
        }}>reset password</button>
        {msg && <div className={msg.bad ? "text-rose-700" : "text-emerald-700"}>{msg.text}</div>}
      </td>
    </tr>
  );
}

export function UsersPage() {
  const api = useApi();
  const { user } = useApp();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.listUsers().then(setUsers).catch((e) => setErr(String(e.message || e)));
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [api]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-sm text-slate-500">Who may sign in to this admin and what each may do. A change applies within 30 seconds, also to a session already open.</p>
        </div>
        <NewUser onDone={load} />
      </div>
      {err && <p className="text-sm text-rose-700">{err}</p>}
      <div className="card overflow-x-auto">
        {!users ? <p className="text-sm text-slate-500">Loading…</p> : (
          <table className="table">
            <thead><tr><th>User</th><th>Roles</th><th>Status</th><th>Since</th><th /></tr></thead>
            <tbody>{users.map((u) => <UserRow key={u.user_id} u={u} me={user?.user_id} onChanged={load} />)}</tbody>
          </table>
        )}
      </div>
      <div className="card text-xs text-slate-600">
        <div className="mb-1 font-medium">Roles</div>
        <ul className="space-y-0.5">{ROLES.map(([k, l, d]) => <li key={k}><strong>{l}</strong> ({k}): {d}</li>)}</ul>
        <p className="mt-2 text-slate-500">An admin cannot disable themselves or drop their own admin role, and the last active admin stays an admin. Partners and the web app use API keys (Integrations), not users.</p>
      </div>
    </div>
  );
}

export { LABEL as ROLE_NAMES };
